import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient, { API } from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import AdminLayout from '@/components/AdminLayout';
import { ArrowLeft, CheckCircle, XCircle, Clock, DollarSign, FileText, TrendingUp, Eye, ExternalLink, Loader2, Send, Mail, CreditCard, Wallet, Users, RotateCcw, AlertTriangle } from 'lucide-react';

const RECON_TYPE_LABELS = {
  reverse_verification: 'Reverse Verification',
  correct_verification: 'Correct Verification',
  duplicate_payment: 'Duplicate Payment',
  wrong_member: 'Wrong Member',
  wrong_invoice: 'Wrong Invoice',
  other: 'Other',
};

const AdminMemberPayments = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingPOP, setViewingPOP] = useState(null);
  const [popImageUrl, setPopImageUrl] = useState(null);
  const [popLoading, setPopLoading] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(null);
  const [markingInvoice, setMarkingInvoice] = useState(null);

  // Reconciliation state
  const [reconciliations, setReconciliations] = useState([]);
  const [reconcilingPayment, setReconcilingPayment] = useState(null);
  const [reconForm, setReconForm] = useState({ reconciliation_type: 'duplicate_payment', reason: '', notes: '', adjustment_amount: '' });
  const [submittingRecon, setSubmittingRecon] = useState(false);

  useEffect(() => {
    loadData();
  }, [memberId]);

  const loadData = async () => {
    try {
      const [memberRes, paymentsRes, invoicesRes, balanceRes, reconRes] = await Promise.all([
        apiClient.get(`/admin/members/${memberId}`),
        apiClient.get(`/payments/member/${memberId}`),
        apiClient.get(`/invoices/member/${memberId}`),
        apiClient.get(`/payments/member/${memberId}/balance`),
        apiClient.get(`/finance/reconciliations/member/${memberId}`).catch(() => ({ data: { reconciliations: [] } }))
      ]);
      
      setMember(memberRes.data);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data);
      setBalance(balanceRes.data);
      setReconciliations(reconRes.data?.reconciliations || []);
    } catch (error) {
      toast.error('Failed to load member data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentId, status) => {
    try {
      await apiClient.post(`/admin/payments/${paymentId}/verify?status=${status}`);
      toast.success(`Payment ${status === 'verified' ? 'verified' : 'rejected'} successfully`);
      loadData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const viewProofOfPayment = async (payment) => {
    if (!payment.pop_attachment) {
      toast.error('No proof of payment attached to this payment');
      return;
    }
    
    const fileType = payment.pop_attachment.file_type || '';
    const popUrl = `${API}/payments/${payment.payment_id}/pop`;
    
    // For PDFs, open in new tab with auth token in URL as a fallback
    // Note: PDFs are downloaded with the endpoint
    if (fileType.includes('pdf')) {
      try {
        setPopLoading(true);
        const response = await apiClient.get(`/payments/${payment.payment_id}/pop`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      } catch (error) {
        toast.error('Failed to load PDF');
        console.error(error);
      } finally {
        setPopLoading(false);
      }
    } else {
      // For images, fetch with auth and show in modal
      setViewingPOP({
        filename: payment.pop_attachment.filename,
        fileType: fileType,
        paymentId: payment.payment_id
      });
      setPopImageUrl(null);
      setPopLoading(true);
      
      try {
        const response = await apiClient.get(`/payments/${payment.payment_id}/pop`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        setPopImageUrl(blobUrl);
      } catch (error) {
        toast.error('Failed to load proof of payment');
        console.error(error);
        setViewingPOP(null);
      } finally {
        setPopLoading(false);
      }
    }
  };

  const closePopModal = () => {
    if (popImageUrl) {
      URL.revokeObjectURL(popImageUrl);
    }
    setViewingPOP(null);
    setPopImageUrl(null);
  };

  const sendReceipt = async (payment) => {
    if (payment.status !== 'verified') {
      toast.error('Can only send receipts for verified payments');
      return;
    }
    
    setSendingReceipt(payment.payment_id);
    try {
      await apiClient.post(`/admin/payments/${payment.payment_id}/send-receipt`);
      toast.success('Receipt sent successfully');
      loadData(); // Reload to get updated receipt_sent_at
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send receipt';
      toast.error(message);
    } finally {
      setSendingReceipt(null);
    }
  };

  const markInvoiceAsPaid = async (invoiceId) => {
    setMarkingInvoice(invoiceId);
    try {
      await apiClient.put(`/invoices/${invoiceId}/status?status=paid`);
      toast.success('Invoice marked as paid');
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update invoice';
      toast.error(message);
    } finally {
      setMarkingInvoice(null);
    }
  };

  const markInvoiceAsUnpaid = async (invoiceId) => {
    setMarkingInvoice(invoiceId);
    try {
      await apiClient.put(`/invoices/${invoiceId}/status?status=unpaid`);
      toast.success('Invoice marked as unpaid');
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update invoice';
      toast.error(message);
    } finally {
      setMarkingInvoice(null);
    }
  };

  const openReconcileDialog = (payment) => {
    setReconcilingPayment(payment);
    setReconForm({ reconciliation_type: 'duplicate_payment', reason: '', notes: '', adjustment_amount: '' });
  };

  const submitReconciliation = async () => {
    if (!reconcilingPayment) return;
    if (!reconForm.reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    setSubmittingRecon(true);
    try {
      const payload = {
        payment_id: reconcilingPayment.payment_id,
        reconciliation_type: reconForm.reconciliation_type,
        reason: reconForm.reason.trim(),
        notes: reconForm.notes.trim() || null,
      };
      if (reconForm.adjustment_amount !== '' && !isNaN(parseFloat(reconForm.adjustment_amount))) {
        payload.adjustment_amount = parseFloat(reconForm.adjustment_amount);
      }
      await apiClient.post('/finance/reconciliations', payload);
      toast.success('Payment reconciled successfully');
      setReconcilingPayment(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reconcile payment');
    } finally {
      setSubmittingRecon(false);
    }
  };

  const getPaymentReconciliations = (paymentId) => reconciliations.filter(r => r.payment_id === paymentId);

  const [renewing, setRenewing] = useState(false);
  const [confirmRenew, setConfirmRenew] = useState(false);

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const res = await apiClient.post(`/membership/members/${memberId}/renew`);
      toast.success(res.data?.message || 'Membership renewed');
      setConfirmRenew(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew membership');
    } finally {
      setRenewing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      verified: { className: 'bg-green-100 text-green-800', icon: CheckCircle },
      pending: { className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      rejected: { className: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const { className, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getInvoiceStatusBadge = (status) => {
    const config = {
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={config[status] || config.unpaid}>{status.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <AdminLayout currentPage="members" title="Member Payments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const currency = balance?.currency || 'USD';

  return (
    <AdminLayout currentPage="members" title={member ? `${member.first_name} ${member.last_name} - Payments` : 'Member Payments'}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/members')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Members
        </Button>
        {member && (
          <div className="flex items-center gap-3" data-testid="membership-cycle-info">
            {member.cycle_year && (
              <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">
                Cycle {member.cycle_year}
                {member.membership_expiry_date && ` · expires ${new Date(member.membership_expiry_date).toLocaleDateString()}`}
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
              onClick={() => setConfirmRenew(true)}
              data-testid="renew-membership-btn"
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Renew Membership
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Balance Summary - Using same breakdown card as member view */}
        {balance && (
          <>
            {/* Payment Summary Card - Same as Member View */}
            <Card className="border-2">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Payment Summary
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Member payment overview
                  {balance.total_lives > 1 && (
                    <span className="ml-2 text-blue-600 font-medium">
                      ({balance.total_lives} lives: 1 member + {balance.num_beneficiaries || (balance.total_lives - 1)} beneficiaries)
                    </span>
                  )}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-700 font-medium">Total Annual Fee</p>
                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(balance.total_annual_fee, currency)}</p>
                    <p className="text-xs text-blue-600 mt-1">
                      {formatCurrency(balance.monthly_fee, currency)}/month × {balance.membership_period_months || 12} months
                    </p>
                    {balance.total_lives > 1 && balance.base_monthly_fee && (
                      <p className="text-xs text-blue-500 mt-0.5">
                        ({formatCurrency(balance.base_monthly_fee, currency)} × {balance.total_lives} lives)
                      </p>
                    )}
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-700 font-medium">Total Paid</p>
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(balance.total_paid, currency)}</p>
                    <p className="text-xs text-green-600 mt-1">{payments.filter(p => p.status === 'verified').length} verified payment(s)</p>
                  </div>
                  
                  <div className={`rounded-lg p-4 border ${balance.outstanding_balance > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${balance.outstanding_balance > 0 ? 'text-red-700' : 'text-green-700'}`}>Outstanding Balance</p>
                    <p className={`text-2xl font-bold ${balance.outstanding_balance > 0 ? 'text-red-800' : 'text-green-800'}`}>
                      {formatCurrency(balance.outstanding_balance, currency)}
                    </p>
                    <p className={`text-xs mt-1 ${balance.outstanding_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {balance.outstanding_balance > 0 ? `${balance.unpaid_invoices_count || 0} invoice(s) pending` : 'All paid up!'}
                    </p>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-700 font-medium">Payment Progress</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {balance.total_annual_fee > 0 
                        ? Math.round((balance.total_paid / balance.total_annual_fee) * 100)
                        : 0}%
                    </p>
                    <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${Math.min(100, balance.total_annual_fee > 0 ? (balance.total_paid / balance.total_annual_fee) * 100 : 0)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Unpaid Invoices</p>
                      <p className="text-2xl font-bold">{balance.unpaid_invoices_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Total Lives</p>
                      <p className="text-2xl font-bold">{balance.total_lives || 1}</p>
                      <p className="text-xs text-muted-foreground">
                        1 member {balance.num_beneficiaries > 0 ? `+ ${balance.num_beneficiaries} beneficiaries` : ''}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Membership Type</p>
                    <p className="text-xl font-bold">{balance.membership_type || 'Standard'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Invoices Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Invoices</CardTitle>
            <Button onClick={() => navigate(`/admin/members/${memberId}/generate-invoice`)}>
              <FileText className="w-4 h-4 mr-2" />
              Generate Invoice
            </Button>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No invoices found</p>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <div key={invoice.invoice_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{invoice.invoice_number}</p>
                      <p className="text-sm text-muted-foreground">{invoice.description}</p>
                      {balance?.show_lives_breakdown && (
                        <p className="text-xs text-blue-700" data-testid={`invoice-lives-${invoice.invoice_id}`}>
                          {formatCurrency(invoice.per_life_fee ?? balance.base_monthly_fee, currency)} per life ×{' '}
                          {invoice.lives ?? balance.total_lives} live{(invoice.lives ?? balance.total_lives) === 1 ? '' : 's'} ×{' '}
                          {invoice.months_billed ?? 1} month{(invoice.months_billed ?? 1) === 1 ? '' : 's'}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-bold">{formatCurrency(invoice.amount, currency)}</p>
                      {getInvoiceStatusBadge(invoice.status)}
                      {/* Mark as Paid/Unpaid Button */}
                      {invoice.status === 'unpaid' || invoice.status === 'overdue' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markInvoiceAsPaid(invoice.invoice_id)}
                          disabled={markingInvoice === invoice.invoice_id}
                          title="Mark as Paid"
                          data-testid={`mark-paid-${invoice.invoice_id}`}
                        >
                          {markingInvoice === invoice.invoice_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-1" />
                              Mark Paid
                            </>
                          )}
                        </Button>
                      ) : invoice.status === 'paid' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => markInvoiceAsUnpaid(invoice.invoice_id)}
                          disabled={markingInvoice === invoice.invoice_id}
                          title="Mark as Unpaid"
                          data-testid={`mark-unpaid-${invoice.invoice_id}`}
                        >
                          {markingInvoice === invoice.invoice_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Undo
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payments Section */}
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No payments found</p>
            ) : (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.payment_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{formatCurrency(payment.amount, currency)}</p>
                        {getStatusBadge(payment.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {payment.payment_method} {payment.payment_reference && `- Ref: ${payment.payment_reference}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(payment.created_at).toLocaleString()}
                      </p>
                      {payment.verification_notes && (
                        <p className="text-xs text-muted-foreground mt-1 italic">
                          Note: {payment.verification_notes}
                        </p>
                      )}
                      {/* Reconciliation entries for this payment */}
                      {getPaymentReconciliations(payment.payment_id).map((rec) => (
                        <div
                          key={rec.reconciliation_id}
                          className="mt-2 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2"
                          data-testid={`reconciliation-${rec.reconciliation_id}`}
                        >
                          <RotateCcw className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div className="text-xs">
                            <p className="font-semibold text-orange-800">
                              {RECON_TYPE_LABELS[rec.reconciliation_type] || rec.reconciliation_type}: {formatCurrency(rec.adjustment_amount, currency)}
                            </p>
                            <p className="text-orange-700">Reason: {rec.reason}</p>
                            {rec.notes && <p className="text-orange-600">Notes: {rec.notes}</p>}
                            <p className="text-orange-500 mt-0.5">
                              By {rec.created_by_name} · {new Date(rec.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 items-center">
                      {/* View Proof of Payment Button - Available for all statuses */}
                      {payment.pop_attachment && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => viewProofOfPayment(payment)}
                          title="View Proof of Payment"
                          data-testid={`view-pop-${payment.payment_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View POP
                        </Button>
                      )}
                      {payment.status === 'pending' && (
                        <>
                          <Button size="sm" variant="default" onClick={() => verifyPayment(payment.payment_id, 'verified')}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => verifyPayment(payment.payment_id, 'rejected')}>
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {/* Send Receipt Button - For verified payments */}
                      {payment.status === 'verified' && (
                        <Button 
                          size="sm" 
                          variant={payment.receipt_sent_at ? "ghost" : "outline"}
                          onClick={() => sendReceipt(payment)}
                          disabled={sendingReceipt === payment.payment_id}
                          title={payment.receipt_sent_at ? `Receipt sent on ${new Date(payment.receipt_sent_at).toLocaleString()}` : "Send Receipt Email"}
                          data-testid={`send-receipt-${payment.payment_id}`}
                        >
                          {sendingReceipt === payment.payment_id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : payment.receipt_sent_at ? (
                            <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                          ) : (
                            <Mail className="w-4 h-4 mr-1" />
                          )}
                          {payment.receipt_sent_at ? 'Sent' : 'Send Receipt'}
                        </Button>
                      )}
                      {/* Reconcile Button - For verified payments */}
                      {payment.status === 'verified' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          onClick={() => openReconcileDialog(payment)}
                          title="Reconcile / correct this verified payment"
                          data-testid={`reconcile-${payment.payment_id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reconcile
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* POP Viewer Modal */}
      <Dialog open={!!viewingPOP} onOpenChange={closePopModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Proof of Payment</DialogTitle>
          </DialogHeader>
          {viewingPOP && (
            <div className="flex flex-col items-center">
              {popLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading...</span>
                </div>
              ) : viewingPOP.fileType?.includes('image') && popImageUrl ? (
                <img
                  src={popImageUrl}
                  alt="Proof of Payment"
                  className="max-h-[60vh] object-contain rounded"
                />
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">PDF Document</p>
                </div>
              )}
              <p className="text-sm text-muted-foreground mt-2">{viewingPOP.filename}</p>
              {popImageUrl && (
                <Button
                  className="mt-4"
                  onClick={() => window.open(popImageUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in New Tab
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reconciliation Dialog */}
      <Dialog open={!!reconcilingPayment} onOpenChange={(open) => !open && setReconcilingPayment(null)}>
        <DialogContent data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              Reconcile Payment
            </DialogTitle>
          </DialogHeader>
          {reconcilingPayment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Original payment</span>
                  <span className="font-bold">{formatCurrency(reconcilingPayment.amount, currency)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{reconcilingPayment.payment_method} · {new Date(reconcilingPayment.created_at).toLocaleDateString()}</p>
              </div>

              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>The original payment record stays intact. A reconciliation adjustment is added to correct the member&apos;s balance and is fully audited.</span>
              </div>

              <div>
                <Label>Reconciliation Type</Label>
                <Select
                  value={reconForm.reconciliation_type}
                  onValueChange={(v) => setReconForm({ ...reconForm, reconciliation_type: v })}
                >
                  <SelectTrigger data-testid="recon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(RECON_TYPE_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Reason <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder="Explain why this payment is being reconciled..."
                  value={reconForm.reason}
                  onChange={(e) => setReconForm({ ...reconForm, reason: e.target.value })}
                  data-testid="recon-reason-input"
                />
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={reconForm.notes}
                  onChange={(e) => setReconForm({ ...reconForm, notes: e.target.value })}
                  data-testid="recon-notes-input"
                />
              </div>

              <div>
                <Label>Adjustment Amount (Optional)</Label>
                <Input
                  type="number"
                  placeholder={`Leave blank for full reversal (−${reconcilingPayment.amount})`}
                  value={reconForm.adjustment_amount}
                  onChange={(e) => setReconForm({ ...reconForm, adjustment_amount: e.target.value })}
                  data-testid="recon-amount-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use a negative number to reduce the paid amount (e.g. −{reconcilingPayment.amount} to reverse). Leave blank to fully reverse this payment.
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setReconcilingPayment(null)} data-testid="recon-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={submitReconciliation}
                  disabled={submittingRecon}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="recon-submit-btn"
                >
                  {submittingRecon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  {submittingRecon ? 'Processing...' : 'Reconcile Payment'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Renew Membership Confirmation */}
      <Dialog open={confirmRenew} onOpenChange={setConfirmRenew}>
        <DialogContent data-testid="renew-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Renew Membership
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This enrolls <span className="font-medium">{member?.first_name} {member?.last_name}</span> into
              the next annual membership cycle and generates a renewal invoice for the full annual fee
              (no proration). The current cycle is <span className="font-medium">{member?.cycle_year || 'unassigned'}</span>.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmRenew(false)} data-testid="renew-cancel-btn">Cancel</Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleRenew}
                disabled={renewing}
                data-testid="renew-confirm-btn"
              >
                {renewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                {renewing ? 'Renewing...' : 'Confirm Renewal'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminMemberPayments;
